import { Heart, Shield, Sparkles, Swords } from "lucide-react";

// Always-visible top-left HUD for players (never shown to the GM) so combat-critical stats stay
// on screen without needing to open the character sheet or tap your token.
export default function PlayerHud({ token, character, stats }) {
  if (!token && !character) return null;

  const hpCurrent = token?.hp_current;
  const hpMax = token?.hp_max;
  const hasHp = hpCurrent != null && hpMax != null;
  const hpPct = hasHp && hpMax > 0 ? Math.max(0, Math.min(100, (hpCurrent / hpMax) * 100)) : 0;

  const ac = stats?.ac ?? character?.dnd5e?.ac ?? null;
  const weaponDrawn = token?.weapon_drawn !== false;

  const slots = character?.spell_slots || {};
  const slotTotals = Object.values(slots).reduce(
    (acc, s) => (s?.max > 0 ? { max: acc.max + s.max, used: acc.used + (s.used || 0) } : acc),
    { max: 0, used: 0 }
  );
  const hasSlots = slotTotals.max > 0;

  return (
    <div
      data-testid="player-hud"
      className="absolute top-4 left-4 z-20 flex items-center gap-2 bg-black/60 backdrop-blur-2xl border border-white/10 rounded-full px-3 py-2"
    >
      {hasHp && (
        <div data-testid="player-hud-hp" className="flex items-center gap-1.5">
          <Heart className="w-3.5 h-3.5 text-[#D90429] shrink-0" />
          <div className="w-16 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-[#D90429] transition-all duration-300" style={{ width: `${hpPct}%` }} />
          </div>
          <span className="text-[11px] font-mono-sheet text-[#f1e0c5]/80 tabular-nums">{hpCurrent}/{hpMax}</span>
        </div>
      )}
      {ac != null && (
        <div data-testid="player-hud-ac" className="flex items-center gap-1 border-l border-white/10 pl-2">
          <Shield className="w-3.5 h-3.5 text-[#7dd3fc]" />
          <span className="text-[11px] font-mono-sheet text-[#f1e0c5]/80">{ac}</span>
        </div>
      )}
      {hasSlots && (
        <div data-testid="player-hud-spell-slots" className="flex items-center gap-1 border-l border-white/10 pl-2">
          <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
          <span className="text-[11px] font-mono-sheet text-[#f1e0c5]/80">{slotTotals.max - slotTotals.used}/{slotTotals.max}</span>
        </div>
      )}
      {token && (
        <div data-testid="player-hud-weapon-status" className={`flex items-center gap-1 border-l border-white/10 pl-2 ${weaponDrawn ? "text-[#4d7c0f]" : "text-[#f1e0c5]/40"}`}>
          <Swords className="w-3.5 h-3.5" />
          <span className="text-[9px] uppercase tracking-wider">{weaponDrawn ? "Drawn" : "Stowed"}</span>
        </div>
      )}
    </div>
  );
}
